import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import Button, { ButtonToneProps } from 'src/components/ui/Button/Button';
import Icon from 'src/components/ui/Icon/Icon';

const ErrorFallback: React.FC<{ error: Error; onReset: () => void }> = ({ error, onReset }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const message = `${error.name}: ${error.message}`;

  return (
    <div className="flex flex-col items-center justify-center flex-1 gap-16 p-24 text-center">
      <Icon name="bug" width={40} height={40} className="text-faded" />
      <h2 className="typo-title-24 text-full">{t('bug_report.error_page.title')}</h2>
      <p className="typo-text-14 text-faded max-w-[480px]">{t('bug_report.error_page.message')}</p>
      <pre className="typo-text-12 text-faded whitespace-pre-wrap max-w-[640px] rounded-base border-subtle px-12 py-8">
        {message}
      </pre>
      <div className="flex items-center gap-8">
        <Button
          tone={ButtonToneProps.SECONDARY}
          title={t('bug_report.error_page.home')}
          onClick={() => {
            onReset();
            navigate('/dashboard');
          }}
        />
        <Button
          tone={ButtonToneProps.SECONDARY}
          title={t('bug_report.error_page.reload')}
          onClick={() => window.location.reload()}
        />
      </div>
    </div>
  );
};

type BoundaryProps = { children: React.ReactNode; resetKey: string };
type BoundaryState = { error: Error | null };

class Boundary extends React.Component<BoundaryProps, BoundaryState> {
  constructor(props: BoundaryProps) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error: Error): BoundaryState {
    return { error };
  }

  componentDidUpdate(prevProps: BoundaryProps): void {
    const { resetKey } = this.props;
    const { error } = this.state;
    if (prevProps.resetKey !== resetKey && error) this.reset();
  }

  reset = (): void => this.setState({ error: null });

  render(): React.ReactNode {
    const { error } = this.state;
    const { children } = this.props;
    if (error) return <ErrorFallback error={error} onReset={this.reset} />;
    return children;
  }
}

const ErrorBoundary: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  return <Boundary resetKey={location.pathname}>{children}</Boundary>;
};

export default ErrorBoundary;
